<script setup>
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const { t, locale } = useI18n()
const story = await useAsyncStoryblok('components/layout/footer', { version: 'publish', language: locale.value })

const activeIndex = ref(null)
const isActive = ref(false)

function onClick(index) {
  activeIndex.value = index
  isActive.value = !isActive.value
}

function onClose() {
  activeIndex.value = null
  isActive.value = false
}
</script>

<template>
  <div>
    <footer>
      <div class="relative bg-primary py-custom-xl lg:py-custom-3xl lg:py-custom-4xl">
        <div class="container">
          <div class="flex flex-wrap justify-between gap-x-10 gap-y-10 2xl:grid 2xl:grid-flow-col 2xl:gap-x-12 2xl:gap-x-16 xl:gap-x-8">
            <div v-for="(menu, index) in story.content.menu" :key="menu._uid" class="footer-submenu-item flex-auto">
              <div v-if="index === 0" class="mb-6">
                <div class="fill-white" v-html="story.content.logo" />
              </div>
              <div class="mb-6 text-lg text-white font-bold uppercase">
                {{ menu.label }}
              </div>
              <div v-for="(submenu, index_lv2) in menu.submenu" :key="submenu._uid" class="text-base text-white capitalize [&:not(:last-child)]:mb-4">
                <a v-if="submenu.target && submenu?.media?.filename || submenu.target" :aria-label="submenu.label" :href="submenu?.media?.filename ? submenu?.media?.filename : submenu.link.cached_url && !submenu.link ? submenu.link.cached_url : submenu.link" target="_blank" class="!no-underline hover:!underline">
                  {{ submenu.label }}
                </a>
                <div v-else class="flex items-center whitespace-nowrap">
                  <NuxtLink
                    v-if="submenu?.query"
                    :aria-label="submenu?.label"
                    :to="localePath({ path: submenu?.link.cached_url && !submenu?.link ? submenu?.link.cached_url : submenu?.link, query: { value: submenu?.query ? submenu?.label : '' } })"
                    :class="submenu?.link.id !== '' ? 'hover:underline' : ''"
                  >
                    {{ submenu?.label }}
                  </NuxtLink>
                  <NuxtLink
                    v-else
                    :aria-label="submenu.label"
                    :to="localePath({ path: submenu.link.cached_url && !submenu.link ? submenu.link.cached_url : submenu.link })"
                    :class="submenu.link.id !== '' ? 'hover:underline' : ''"
                  >
                    {{ submenu.label }}
                  </NuxtLink>
                  <div
                    v-if="submenu.submenu.length"
                    class="w-1/2 flex cursor-pointer justify-end pl-6 text-[10px] text-white hover:text-secondary-red-100"
                    @click="onClick(index_lv2)"
                  >
                    <svg
                      class="transition-all duration-200 ease-in-out"
                      :class="activeIndex === index_lv2 && isActive ? '-rotate-180' : 'not-active rotate-0 '"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20" height="20" viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M8.12 9.29L12 13.17l3.88-3.88a.996.996 0 1 1 1.41 1.41l-4.59 4.59a.996.996 0 0 1-1.41 0L6.7 10.7a.996.996 0 0 1 0-1.41c.39-.38 1.03-.39 1.42 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  v-if="activeIndex === index_lv2 && isActive"
                  class="submenu--list-submenu relative overflow-hidden"
                >
                  <NuxtLink
                    v-for="(submenu_lv3, index_lv3) in submenu.submenu" :key="submenu_lv3._uid"
                    :to="localePath({ path: submenu_lv3.link ? submenu_lv3.link : '', query: { value: submenu_lv3.query ? submenu_lv3.label : '' } })"
                    @click="onClose"
                  >
                    <div class="flex py-1.5 hover:text-secondary-red-100">
                      <span class="ml-3 flex items-center font-medium">
                        <svg
                          class="mr-1.5" xmlns="http://www.w3.org/2000/svg" width="7" height="7"
                          viewBox="0 0 16 16"
                        >
                          <path fill="currentColor" d="M8 4a4 4 0 1 1 0 8a4 4 0 0 1 0-8Z" />
                        </svg>
                        {{ submenu_lv3.label }}
                      </span>
                    </div>
                  </NuxtLink>
                </div>
              </div>
              <div v-for="submenu in menu.submenu2" v-if="menu?.submenu2?.length > 0" :key="submenu._uid">
                <p class="mb-5 text-white" :to="localePath({ path: submenu.link })">
                  <span>{{ submenu.label }}</span>
                </p>
                <FormSubscribe v-if="submenu.component === 'Input'" :label="submenu.placeholder" />
              </div>
              <div v-if="menu?.submenu3?.length > 0" class="mt-6 flex flex-col gap-1.5">
                <div class="flex flex-wrap gap-x-4">
                  <div v-for="submenu in menu.submenu3" v-show="submenu.icon" :key="submenu._uid">
                    <a
                      v-if="submenu.icon"
                      :href="submenu.link ? submenu.link.url : submenu.link.cached_url" target="_blank"
                      rel="noopener"
                      aria-label="Social Media"
                      class="h-[50px] w-[50px] flex items-center justify-center rounded-full bg-[#261A67]"
                      v-html="submenu.icon"
                    />
                  </div>
                </div>
                <div class="mt-4 flex flex-wrap gap-x-3 gap-y-4">
                  <div v-for="submenu in menu.submenu3" v-show="submenu.media.filename" :key="submenu._uid">
                    <a v-if="submenu.media.filename" :href="submenu.link ? submenu.link.cached_url : submenu.link.cached_url" target="_blank" class="text-white">
                      <nuxt-img loading="lazy" alt="Login Page" :src="`${submenu.media.filename}`" />
                    </a>
                  </div>
                </div>
              </div>
              <div v-if="index === 0" class="grid mt-6">
                <div v-if="story.content.contact" class="[&>*]:text-white [&>p]:leading-9">
                  <RichTextRenderer :document="story.content.contact" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-red-gradient py-3">
        <div class="container">
          <div class="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-normal lg:gap-x-10">
            <div class="flex flex-col gap-x-10 sm:flex-row sm:flex-wrap lg:flex-nowrap sm:items-center">
              <NuxtLink
                :to="localePath({
                  path: `${story.content.terms.cached_url ? story.content.terms.cached_url : story.content.terms.url}`,
                })"
                class="whitespace-nowrap text-white"
              >
                {{ t('footer.terms') }}
              </NuxtLink>
              <NuxtLink
                :to="localePath(
                  {
                    path: `${story.content.policy.cached_url ? story.content.policy.cached_url : story.content.policy.url}`,
                  })
                "
                class="whitespace-nowrap text-white"
              >
                {{ t('footer.privacy') }}
              </NuxtLink>
              <a :href="story.content.cookies.filename" target="_blank" class="whitespace-nowrap text-white">
                {{ t('footer.cookies') }}
              </a>
              <!-- <a href="" target="_blank" class="text-white whitespace-nowrap">
                {{ t('footer.sitemap') }}
              </a> -->
            </div>
            <div class="lg:ml-auto">
              <p class="text-white">
                {{ story.content.copyright }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.footer-submenu-item:last-child {
  @apply w-full col-span-6 w-full lg:w-auto md:w-2/2;
}
</style>
